// SC-1752 — pure rules of the guest survey page (spec §3, §5, §8).
import type {
  AnswerMap,
  AnswerState,
  LocalizedText,
  SubmitAnswer,
  SurveyDish,
  SurveyQuestion,
  SurveySchema,
  SurveySection,
} from './types';

export const emptyAnswer = (): AnswerState => ({
  optionIds: [],
  optionTexts: {},
  text: '',
  date: '',
  dishes: [],
});

/** Requested language → default language → empty (same rule on every surface). */
export function pickText(
  text: LocalizedText | undefined | null,
  lang: string,
  defaultLang: string,
): string {
  if (!text) return '';
  return text[lang] || text[defaultLang] || '';
}

const CHOICE_TYPES = ['single_choice', 'multi_choice', 'rating'];

export function isAnswered(q: SurveyQuestion, a: AnswerState | undefined): boolean {
  if (!a) return false;
  switch (q.type) {
    case 'single_choice':
    case 'multi_choice':
    case 'rating':
      return a.optionIds.length > 0;
    case 'short_text':
    case 'long_text':
      return a.text.trim() !== '';
    case 'date':
      return a.date !== '';
    case 'dish':
      return a.dishes.length > 0 || (a.pending ?? '').trim() !== '';
  }
}

/**
 * Visibility is computed forward in question order: a question is shown when it
 * has no showIf, its parent is itself visible, and the parent has one of the
 * wanted options selected.
 */
export function visibleQuestionIds(schema: SurveySchema, answers: AnswerMap): Set<string> {
  const visible = new Set<string>();
  for (const section of schema.sections) {
    for (const q of section.questions) {
      const cond = q.showIf;
      if (!cond) {
        visible.add(q.id);
        continue;
      }
      if (!visible.has(cond.questionId)) continue;
      const picked = answers[cond.questionId]?.optionIds ?? [];
      if (picked.some((id) => cond.optionIds.includes(id))) visible.add(q.id);
    }
  }
  return visible;
}

/** Sections that still have something to ask (an all-hidden section has no screen). */
export function visibleSections(
  schema: SurveySchema,
  visible: Set<string>,
): { section: SurveySection; questions: SurveyQuestion[] }[] {
  return schema.sections
    .map((section) => ({
      section,
      questions: section.questions.filter((q) => visible.has(q.id)),
    }))
    .filter((s) => s.questions.length > 0);
}

export function missingRequired(schema: SurveySchema, answers: AnswerMap): string[] {
  const visible = visibleQuestionIds(schema, answers);
  const missing: string[] = [];
  for (const section of schema.sections) {
    for (const q of section.questions) {
      if (q.required && visible.has(q.id) && !isAnswered(q, answers[q.id])) missing.push(q.id);
    }
  }
  return missing;
}

/** Payload answers: hidden questions and empty answers are dropped. */
export function serializeAnswers(schema: SurveySchema, answers: AnswerMap): SubmitAnswer[] {
  const visible = visibleQuestionIds(schema, answers);
  const out: SubmitAnswer[] = [];
  for (const section of schema.sections) {
    for (const q of section.questions) {
      const a = answers[q.id];
      if (!visible.has(q.id) || !isAnswered(q, a)) continue;
      if (CHOICE_TYPES.includes(q.type)) {
        const entry: SubmitAnswer = { question_id: q.id, option_ids: a.optionIds };
        const texts: Record<string, string> = {};
        for (const id of a.optionIds) {
          const wantsText = q.options?.find((o) => o.id === id)?.hasText;
          const value = (a.optionTexts[id] ?? '').trim();
          if (wantsText && value) texts[id] = value;
        }
        if (Object.keys(texts).length) entry.option_texts = texts;
        out.push(entry);
      } else if (q.type === 'date') {
        out.push({ question_id: q.id, date: a.date });
      } else if (q.type === 'dish') {
        out.push({ question_id: q.id, dishes: a.dishes });
      } else {
        out.push({ question_id: q.id, text: a.text.trim() });
      }
    }
  }
  return out;
}

/** Lower-case, accent-free form used to compare what the guest types with dish names. */
export function normalizeSearch(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();
}

export function filterDishes(dishes: SurveyDish[], query: string, limit = 5): SurveyDish[] {
  const needle = normalizeSearch(query);
  if (!needle) return [];
  return dishes.filter((d) => normalizeSearch(d.name).includes(needle)).slice(0, limit);
}

/** Answers a fresh page starts from: `defaultToday` date questions are pre-filled. */
export function initialAnswers(schema: SurveySchema, today: string): AnswerMap {
  const out: AnswerMap = {};
  for (const section of schema.sections) {
    for (const q of section.questions) {
      if (q.type === 'date' && q.defaultToday) out[q.id] = { ...emptyAnswer(), date: today };
    }
  }
  return out;
}

/**
 * A draft written on an older version: keep what the current schema can still
 * accept — answers to questions that exist, option ids that exist.
 */
export function pruneAnswers(schema: SurveySchema, answers: AnswerMap): AnswerMap {
  const out: AnswerMap = {};
  for (const section of schema.sections) {
    for (const q of section.questions) {
      const a = answers[q.id];
      if (!a) continue;
      if (q.type === 'single_choice' || q.type === 'multi_choice' || q.type === 'rating') {
        const known = new Set((q.options ?? []).map((o) => o.id));
        const optionIds = a.optionIds.filter((id) => known.has(id));
        const optionTexts = Object.fromEntries(Object.entries(a.optionTexts).filter(([id]) => known.has(id)));
        out[q.id] = { ...a, optionIds, optionTexts };
      } else {
        out[q.id] = a;
      }
    }
  }
  return out;
}

export function localToday(now: Date = new Date()): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
}

const MAX_DISHES = 30;

/**
 * A dish name typed but never added ("Gyoza" then Suivant) is turned into a tag:
 * an exact catalogue match keeps its product uuid, anything else goes in as free text.
 */
export function commitPendingDishes(
  schema: SurveySchema,
  answers: AnswerMap,
  dishes: SurveyDish[],
): AnswerMap {
  let out = answers;
  for (const section of schema.sections) {
    for (const q of section.questions) {
      const a = answers[q.id];
      if (q.type !== 'dish' || !a || a.pending === undefined || a.pending === '') continue;
      const name = a.pending.trim();
      const key = normalizeSearch(name);
      let next = a.dishes;
      if (name && !a.dishes.some((d) => normalizeSearch(d.name) === key)) {
        const match = dishes.find((d) => normalizeSearch(d.name) === key);
        const dish = match ? { product_uuid: match.uuid, name: match.name } : { product_uuid: null, name };
        next = q.multiple ? [...a.dishes, dish].slice(0, MAX_DISHES) : [dish];
      }
      if (out === answers) out = { ...answers };
      out[q.id] = { ...a, dishes: next, pending: '' };
    }
  }
  return out;
}

/** A date alone (typically the pre-filled « today ») is not an answer. */
export function hasRealAnswer(list: SubmitAnswer[]): boolean {
  return list.some((a) => a.date === undefined);
}

// ---- theme guard -----------------------------------------------------------

function luminance(hex: string | undefined): number | null {
  let h = (hex ?? '').replace('#', '');
  if (/^[0-9a-fA-F]{3}$/.test(h)) h = h.split('').map((c) => c + c).join('');
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
  const lin = (c: number) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  return (
    0.2126 * lin(parseInt(h.slice(0, 2), 16)) +
    0.7152 * lin(parseInt(h.slice(2, 4), 16)) +
    0.0722 * lin(parseInt(h.slice(4, 6), 16))
  );
}

export function contrastRatio(a: string | undefined, b: string | undefined): number | null {
  const la = luminance(a);
  const lb = luminance(b);
  if (la === null || lb === null) return null;
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

interface ThemeColors {
  textPrimary?: string;
  textSecondary?: string;
  cardBackground?: string;
  pageBackground?: string;
}

// colors_and_type.css defaults, used when the theme sets none of them.
const DEFAULT_INK = '#222222';
const DEFAULT_PAPER = '#FFFFFF';
const DEFAULT_CREAM = '#FAFFEE';

/**
 * Some themes carry text colours that vanish on their own cards (white on
 * white). The survey is read by guests who never chose that theme, so when the
 * ink fails 4.5:1 against the card or page colour it is swapped for whichever of
 * dark / white reads best on both; returns the CSS variables to override.
 */
export function surveyInkVars(theme: ThemeColors | null | undefined): Record<string, string> {
  const paper = theme?.cardBackground || DEFAULT_PAPER;
  const cream = theme?.pageBackground || DEFAULT_CREAM;
  const ink = theme?.textPrimary || DEFAULT_INK;
  const worst = (fg: string) => Math.min(contrastRatio(fg, paper) ?? 21, contrastRatio(fg, cream) ?? 21);
  const out: Record<string, string> = {};
  let safe = ink;
  if (worst(ink) < 4.5) {
    safe = worst('#1D1E1E') >= worst('#FFFFFF') ? '#1D1E1E' : '#FFFFFF';
    out['--ink'] = safe;
  }
  const mute = theme?.textSecondary;
  if (out['--ink'] || (mute && worst(mute) < 4.5)) out['--ink-mute'] = safe;
  return out;
}

/** Potta One and the display serif cover only part of CJK / Arabic: use a bold system face there. */
export function headingStyle(lang: string): { fontFamily: string; fontWeight: number } | undefined {
  return ['zh', 'ja', 'ar'].includes(lang)
    ? { fontFamily: 'system-ui, "Noto Sans", sans-serif', fontWeight: 700 }
    : undefined;
}
